class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.initialCartSynced = false;
    this.initialCartSyncPromise = null;

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') {
        this.close();
      }
    });

    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }


  /* =====================================================
     OVERLAY
  ===================================================== */

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (!overlay) return;

    if (overlay.dataset.cartDrawerBound === 'true') {
      return;
    }

    overlay.dataset.cartDrawerBound = 'true';

    overlay.addEventListener(
      'click',
      this.close.bind(this)
    );
  }


  /* =====================================================
     ICONO DEL CARRITO
  ===================================================== */

  setHeaderCartIconAccessibility() {
    const cartLink =
      document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    /*
      Evita registrar el evento dos veces
      si Shopify vuelve a inicializar contenido.
    */
    if (
      cartLink.dataset.cartDrawerAccessibilityBound ===
      'true'
    ) {
      return;
    }

    cartLink.dataset.cartDrawerAccessibilityBound =
      'true';


    cartLink.addEventListener(
      'click',
      async (event) => {
        event.preventDefault();

        await this.open(cartLink);
      }
    );


    cartLink.addEventListener(
      'keydown',
      async (event) => {
        const code =
          event.code.toUpperCase();

        if (
          code === 'SPACE' ||
          code === 'ENTER'
        ) {
          event.preventDefault();

          await this.open(cartLink);
        }
      }
    );
  }


  /* =====================================================
     SINCRONIZAR CARRITO CON SHOPIFY
     ANTES DE LA PRIMERA APERTURA
  ===================================================== */

  async syncInitialCart() {
    /*
      Ya sincronizado.
    */
    if (this.initialCartSynced) {
      return;
    }


    /*
      Si ya existe una petición,
      reutilizamos la misma.
    */
    if (this.initialCartSyncPromise) {
      return this.initialCartSyncPromise;
    }


    this.initialCartSyncPromise =
      this.fetchFreshCartDrawer();


    try {
      await this.initialCartSyncPromise;

      this.initialCartSynced = true;
    } catch (error) {
      /*
        Si Shopify falla temporalmente,
        permitimos abrir el carrito existente
        en vez de bloquearlo.
      */
      console.warn(
        'Prime Perfumes: no se pudo sincronizar el carrito inicialmente.',
        error
      );
    } finally {
      this.initialCartSyncPromise = null;
    }
  }


  /* =====================================================
     PEDIR CART DRAWER ACTUALIZADO A SHOPIFY
  ===================================================== */

  async fetchFreshCartDrawer() {
    const cartUrl =
      typeof routes !== 'undefined' &&
      routes.cart_url
        ? routes.cart_url
        : '/cart';


    const separator =
      cartUrl.includes('?')
        ? '&'
        : '?';


    const url =
      `${cartUrl}${separator}section_id=cart-drawer&_=${Date.now()}`;


    const response =
      await fetch(
        url,
        {
          method: 'GET',

          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          },

          cache: 'no-store'
        }
      );


    if (!response.ok) {
      throw new Error(
        `Error HTTP ${response.status}`
      );
    }


    const responseText =
      await response.text();


    if (!responseText) {
      throw new Error(
        'Shopify devolvió una respuesta vacía.'
      );
    }


    const parsedDocument =
      new DOMParser().parseFromString(
        responseText,
        'text/html'
      );


    const freshCartDrawer =
      parsedDocument.querySelector(
        'cart-drawer'
      );


    const freshCartContent =
      parsedDocument.querySelector(
        '#CartDrawer'
      );


    const currentCartContent =
      this.querySelector(
        '#CartDrawer'
      );


    if (
      !freshCartDrawer ||
      !freshCartContent ||
      !currentCartContent
    ) {
      throw new Error(
        'No se encontró la estructura del Cart Drawer.'
      );
    }


    /*
      Actualizamos estado vacío / con productos.
    */

    this.classList.toggle(
      'is-empty',
      freshCartDrawer.classList.contains(
        'is-empty'
      )
    );


    /*
      Reemplazamos solamente el contenido interno,
      manteniendo el Web Component actual.
    */

    currentCartContent.innerHTML =
      freshCartContent.innerHTML;


    /*
      Después del reemplazo el overlay es nuevo.
      Hay que volver a registrar su evento.
    */

    this.bindOverlay();
  }


  /* =====================================================
     ABRIR
  ===================================================== */

  async open(triggeredBy) {
    /*
      Si ya está abierto,
      no hacemos nada.
    */
    if (this.classList.contains('active')) {
      return;
    }


    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }


    /*
      ESTA ES LA CORRECCIÓN PRINCIPAL:

      La primera vez que el usuario abre el carrito,
      descargamos su estado real desde Shopify antes
      de mostrar el drawer.
    */

    await this.syncInitialCart();


    this.bindOverlay();


    const drawerInner =
      this.querySelector('.drawer__inner');


    if (!drawerInner) {
      console.warn(
        'Prime Perfumes: no existe .drawer__inner.'
      );

      return;
    }


    const cartDrawerNote =
      this.querySelector(
        '[id^="Details-"] summary'
      );


    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(
        cartDrawerNote
      );
    }


    /*
      Permitimos que el navegador pinte
      el contenido actualizado antes
      de ejecutar la animación.
    */

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add(
          'animate',
          'active'
        );
      });
    });


    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains('is-empty')
            ? this.querySelector(
                '.drawer__inner-empty'
              )
            : this.querySelector(
                '#CartDrawer'
              );


        const focusElement =
          this.querySelector(
            '.drawer__inner'
          ) ||
          this.querySelector(
            '.drawer__close'
          );


        if (
          containerToTrapFocusOn &&
          focusElement &&
          typeof trapFocus ===
            'function'
        ) {
          trapFocus(
            containerToTrapFocusOn,
            focusElement
          );
        }
      },
      {
        once: true
      }
    );


    document.body.classList.add(
      'overflow-hidden'
    );
  }


  /* =====================================================
     CERRAR
  ===================================================== */

  close() {
    this.classList.remove('active');

    if (
      typeof removeTrapFocus ===
      'function'
    ) {
      removeTrapFocus(
        this.activeElement
      );
    }

    document.body.classList.remove(
      'overflow-hidden'
    );
  }


  /* =====================================================
     CART NOTE
  ===================================================== */

  setSummaryAccessibility(
    cartDrawerNote
  ) {
    cartDrawerNote.setAttribute(
      'role',
      'button'
    );

    cartDrawerNote.setAttribute(
      'aria-expanded',
      'false'
    );


    const nextElement =
      cartDrawerNote.nextElementSibling;


    if (
      nextElement &&
      nextElement.getAttribute('id')
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        nextElement.id
      );
    }


    cartDrawerNote.addEventListener(
      'click',
      (event) => {
        const details =
          event.currentTarget.closest(
            'details'
          );


        if (!details) return;


        event.currentTarget.setAttribute(
          'aria-expanded',
          !details.hasAttribute('open')
        );
      }
    );


    if (
      cartDrawerNote.parentElement &&
      typeof onKeyUpEscape ===
        'function'
    ) {
      cartDrawerNote.parentElement.addEventListener(
        'keyup',
        onKeyUpEscape
      );
    }
  }


  /* =====================================================
     ACTUALIZAR AL AGREGAR PRODUCTO
  ===================================================== */

  renderContents(parsedState) {
    if (!parsedState) {
      console.warn(
        'Prime Perfumes: renderContents recibió un estado vacío.'
      );

      return;
    }


    const drawerInner =
      this.querySelector(
        '.drawer__inner'
      );


    if (drawerInner) {
      drawerInner.classList.remove(
        'is-empty'
      );
    }


    /*
      Estado general del drawer.
    */
    if (
      typeof parsedState.item_count !==
      'undefined'
    ) {
      this.classList.toggle(
        'is-empty',
        parsedState.item_count === 0
      );
    } else {
      this.classList.remove(
        'is-empty'
      );
    }


    this.productId =
      parsedState.id;


    const sections =
      parsedState.sections || {};


    this.getSectionsToRender().forEach(
      (section) => {
        const sectionElement =
          section.selector
            ? document.querySelector(
                section.selector
              )
            : document.getElementById(
                section.id
              );


        if (!sectionElement) {
          return;
        }


        const sectionHTML =
          sections[section.id];


        if (!sectionHTML) {
          console.warn(
            `Prime Perfumes: Shopify no devolvió ${section.id}.`
          );

          return;
        }


        const newHTML =
          this.getSectionInnerHTML(
            sectionHTML,
            section.selector
          );


        if (
          newHTML === null ||
          typeof newHTML ===
            'undefined'
        ) {
          return;
        }


        sectionElement.innerHTML =
          newHTML;
      }
    );


    /*
      Ya tenemos contenido fresco;
      no necesitamos volver a sincronizar
      cuando el usuario lo abra después.
    */

    this.initialCartSynced =
      true;


    requestAnimationFrame(() => {
      this.bindOverlay();

      requestAnimationFrame(() => {
        this.open();
      });
    });
  }


  /* =====================================================
     EXTRAER HTML DE UNA SECCIÓN
  ===================================================== */

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    if (!html) {
      return null;
    }


    try {
      const parsedDocument =
        new DOMParser().parseFromString(
          html,
          'text/html'
        );


      const element =
        parsedDocument.querySelector(
          selector
        );


      if (!element) {
        console.warn(
          `Prime Perfumes: no se encontró ${selector} en la respuesta de Shopify.`
        );

        return null;
      }


      return element.innerHTML;
    } catch (error) {
      console.error(
        'Prime Perfumes: error procesando sección del carrito.',
        error
      );

      return null;
    }
  }


  /* =====================================================
     SECCIONES
  ===================================================== */

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }


  /* =====================================================
     DOM
  ===================================================== */

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    if (!html) {
      return null;
    }


    try {
      return new DOMParser()
        .parseFromString(
          html,
          'text/html'
        )
        .querySelector(
          selector
        );
    } catch (error) {
      console.error(
        'Prime Perfumes: error creando DOM del carrito.',
        error
      );

      return null;
    }
  }


  /* =====================================================
     ELEMENTO ACTIVO
  ===================================================== */

  setActiveElement(element) {
    this.activeElement =
      element;
  }
}


/* =====================================================
   REGISTRAR CART DRAWER
===================================================== */

if (
  !customElements.get(
    'cart-drawer'
  )
) {
  customElements.define(
    'cart-drawer',
    CartDrawer
  );
}



/* =====================================================
   CART DRAWER ITEMS
===================================================== */

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}


/* =====================================================
   REGISTRAR CART DRAWER ITEMS
===================================================== */

if (
  !customElements.get(
    'cart-drawer-items'
  )
) {
  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}